import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  makeStyles,
  Snackbar,
  Tooltip
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import CloseIcon from '@material-ui/icons/Close'
import EditIcon from '@material-ui/icons/Edit'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import AddIcon from '@material-ui/icons/Add'
import SydButton from '../../../../../commonDesign/Button'
import { dataPipelineVersioning } from '../../../../../../api/coreData'
import { formatRelativeTime } from '../../SmartEndpoints/formatRelativeTime'

// Keep these in sync with the BE copy in
// data-svc-core-data-api/.../dataPipelineTags/constants.js.
const TAG_NAME_PATTERN = /^[a-z0-9]+$/
const RESERVED_TAG_NAMES = new Set(['next'])
const MAX_TAG_NAME_LENGTH = 8

function sortTags (tags) {
  if (!tags) return []
  return [...tags].sort((a, b) => {
    if (a.tagName === 'live') return -1
    if (b.tagName === 'live') return 1
    return a.tagName.localeCompare(b.tagName)
  })
}

const useStyles = makeStyles(() => ({
  dialogPaper: {
    maxWidth: '940px',
    width: '940px',
    minHeight: '560px',
    maxHeight: '85vh',
    borderRadius: 'var(--border-radius-md, 8px)'
  },
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px 12px',
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  titleText: {
    fontSize: 'var(--text-size-lg, 18px)',
    fontWeight: 600,
    color: 'var(--color-neutral-900)',
    fontFamily: 'var(--font-mono, monospace)'
  },
  titleSub: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-400)',
    marginTop: '2px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    fontWeight: 500
  },
  content: {
    padding: '16px 24px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    overflow: 'hidden'
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  sectionTitle: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--color-neutral-500)'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 0 4px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 'var(--text-size-sm)',
    '& th': {
      textAlign: 'left',
      padding: '8px 12px',
      fontSize: 'var(--text-size-xs)',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: 'var(--color-neutral-500)',
      borderBottom: '1px solid var(--color-neutral-200)',
      backgroundColor: 'var(--color-neutral-050)',
      position: 'sticky',
      top: 0,
      zIndex: 1
    },
    '& td': {
      padding: '6px 12px',
      borderBottom: '1px solid var(--color-neutral-100)',
      color: 'var(--color-neutral-800)'
    },
    '& tr:last-child td': { borderBottom: 'none' }
  },
  tagNameCell: {
    fontFamily: 'var(--font-mono, monospace)',
    fontWeight: 600
  },
  versionBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-600)'
  },
  liveBadge: {
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    color: 'var(--color-success-700, #15803d)'
  },
  nextBadge: {
    backgroundColor: 'var(--color-primary-050, #eff6ff)',
    color: 'var(--color-primary-700, #1d4ed8)'
  },
  virtualRow: {
    '& td': { color: 'var(--color-neutral-400)', fontStyle: 'italic' }
  },
  actionIconBtn: {
    padding: 4,
    color: 'var(--color-neutral-300)',
    '&:hover': { color: 'var(--color-primary-600)', backgroundColor: 'var(--color-primary-050, #eff6ff)' }
  },
  deleteIconBtn: {
    padding: 4,
    color: 'var(--color-neutral-300)',
    '&:hover': { color: 'var(--color-danger-600, #dc2626)', backgroundColor: 'var(--color-danger-050, #fef2f2)' }
  },
  formCard: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-md, 8px)',
    padding: '16px 20px',
    backgroundColor: 'var(--color-neutral-050)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  formHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  formTitle: {
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600,
    color: 'var(--color-neutral-800)'
  },
  formRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start'
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1
  },
  formLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  },
  formInput: {
    padding: '7px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    '&:focus': { borderColor: 'var(--color-primary-400)', boxShadow: '0 0 0 2px var(--color-primary-100)' },
    '&:disabled': { backgroundColor: 'var(--color-neutral-050)', color: 'var(--color-neutral-400)' }
  },
  formInputMono: { fontFamily: 'var(--font-mono, monospace)' },
  formSelect: {
    padding: '7px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    cursor: 'pointer',
    '&:focus': { borderColor: 'var(--color-primary-400)', boxShadow: '0 0 0 2px var(--color-primary-100)' }
  },
  formError: {
    fontSize: '11px',
    color: 'var(--color-danger-600, #dc2626)',
    marginTop: '-2px'
  },
  attestRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '4px 0'
  },
  attestCheckbox: {
    marginTop: '2px',
    accentColor: 'var(--color-primary-600)',
    width: '15px',
    height: '15px',
    cursor: 'pointer',
    flexShrink: 0
  },
  attestLabel: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-600)',
    lineHeight: 1.5,
    cursor: 'pointer'
  },
  formActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingTop: '4px'
  },
  emptyText: {
    padding: '16px',
    textAlign: 'center',
    color: 'var(--color-neutral-400)',
    fontSize: 'var(--text-size-sm)',
    fontStyle: 'italic'
  },
  timestampText: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-400)',
    whiteSpace: 'nowrap'
  },
  historyWrap: {
    maxHeight: '220px',
    overflowY: 'auto',
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-sm)'
  },
  actionBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  },
  actionCreate: {
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    color: 'var(--color-success-700, #15803d)'
  },
  actionPromote: {
    backgroundColor: 'var(--color-primary-050, #eff6ff)',
    color: 'var(--color-primary-700, #1d4ed8)'
  },
  actionDelete: {
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    color: 'var(--color-danger-700, #b91c1c)'
  },
  commentText: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-500)',
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block'
  },
  savingOverlay: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-primary-600)'
  }
}))

const INITIAL_FORM = { tagName: '', version: '', comment: '', ticketNumber: '', attested: false }

function validateTagName (raw, existingTagNames) {
  const value = (raw || '').trim()
  if (!value) return 'Required'
  if (value.length > MAX_TAG_NAME_LENGTH) return `Max ${MAX_TAG_NAME_LENGTH} characters`
  if (!TAG_NAME_PATTERN.test(value)) return 'Lowercase letters and digits only'
  if (RESERVED_TAG_NAMES.has(value)) return `'${value}' is reserved`
  if (existingTagNames.includes(value)) return `Tag '${value}' already exists`
  return null
}

function DataPipelineTagManagementModal ({ code, onClose }) {
  const classes = useStyles()
  const [formMode, setFormMode] = useState(null)
  const [form, setForm] = useState(INITIAL_FORM)
  const [toast, setToast] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const confirmDeleteTimerRef = useRef(null)
  useEffect(() => () => {
    if (confirmDeleteTimerRef.current) clearTimeout(confirmDeleteTimerRef.current)
  }, [])

  const { data: tagsData, isLoading: tagsLoading } = dataPipelineVersioning.useListDataPipelineTags(code)
  const { data: historyData, isLoading: historyLoading } = dataPipelineVersioning.useListDataPipelineTagHistory(code)
  const { data: versionsData } = dataPipelineVersioning.useListDataPipelineVersions(code)

  const createTag = dataPipelineVersioning.useCreateDataPipelineTag()
  const promoteTag = dataPipelineVersioning.usePromoteDataPipelineTag()
  const deleteTag = dataPipelineVersioning.useDeleteDataPipelineTag()

  const versions = useMemo(() => {
    if (!versionsData?.versions) return []
    return [...versionsData.versions].sort((a, b) => b.version - a.version)
  }, [versionsData])

  const maxVersion = versions.length > 0 ? versions[0].version : 1
  const sortedTags = useMemo(() => sortTags(tagsData?.tags), [tagsData])

  const versionOptions = useMemo(() => {
    const opts = []
    for (let v = maxVersion; v >= 1; v--) opts.push(v)
    return opts
  }, [maxVersion])

  const existingTagNames = useMemo(() => sortedTags.map(t => t.tagName), [sortedTags])

  const tagNameError = useMemo(() => {
    if (formMode !== 'create' || !form.tagName) return null
    return validateTagName(form.tagName, existingTagNames)
  }, [formMode, form.tagName, existingTagNames])

  const openCreateForm = useCallback(() => {
    setFormMode('create')
    setForm({ ...INITIAL_FORM })
  }, [])

  const openPromoteForm = useCallback((tag) => {
    setFormMode('promote')
    setForm({ tagName: tag.tagName, version: '', comment: '', ticketNumber: '', attested: false })
  }, [])

  const closeForm = useCallback(() => {
    setFormMode(null)
    setForm(INITIAL_FORM)
  }, [])

  const handleFormChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleSubmit = useCallback(async () => {
    const payload = {
      code,
      tagName: form.tagName.trim(),
      version: parseInt(form.version, 10),
      comment: form.comment || null,
      ticketNumber: form.ticketNumber || null
    }
    try {
      if (formMode === 'create') {
        await createTag.mutateAsync(payload)
        setToast({ message: `Tag '${payload.tagName}' created → v${payload.version}`, severity: 'success' })
      } else {
        await promoteTag.mutateAsync(payload)
        setToast({ message: `Tag '${payload.tagName}' promoted to v${payload.version}`, severity: 'success' })
      }
      closeForm()
    } catch (err) {
      setToast({ message: err?.response?.data?.message || err.message || 'Operation failed', severity: 'error' })
    }
  }, [code, form, formMode, createTag, promoteTag, closeForm])

  const handleDelete = useCallback(async (tagName) => {
    try {
      await deleteTag.mutateAsync({ code, tagName })
      setToast({ message: `Tag '${tagName}' deleted`, severity: 'success' })
    } catch (err) {
      setToast({ message: err?.response?.data?.message || err.message || 'Delete failed', severity: 'error' })
    }
  }, [code, deleteTag])

  const isSaving = createTag.isLoading || promoteTag.isLoading || deleteTag.isLoading

  const canSubmit = formMode === 'create'
    ? form.tagName && !tagNameError && form.version && form.attested && !isSaving
    : form.version && form.attested && !isSaving

  return (
    <Dialog open onClose={isSaving ? undefined : onClose} classes={{ paper: classes.dialogPaper }} maxWidth={false}>
      <div className={classes.titleBar}>
        <div>
          <div className={classes.titleText}>{code}</div>
          <div className={classes.titleSub}>Tag Management</div>
        </div>
        <Tooltip title='Close' arrow>
          <IconButton size='small' onClick={onClose} disabled={isSaving}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </div>

      <DialogContent className={classes.content}>
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <span className={classes.sectionTitle}>Tags</span>
            {!formMode && (
              <Tooltip title='Create a new tag' arrow>
                <IconButton size='small' className={classes.actionIconBtn} onClick={openCreateForm}>
                  <AddIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Tag</th>
                <th>Version</th>
                <th>Last Modified</th>
                <th>Modified By</th>
                <th style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody>
              {tagsLoading ? (
                <tr><td colSpan={5} className={classes.emptyText}>Loading…</td></tr>
              ) : (
                <>
                  {sortedTags.map(tag => (
                    <tr key={tag.tagName}>
                      <td className={classes.tagNameCell}>
                        <span className={`${classes.versionBadge} ${tag.tagName === 'live' ? classes.liveBadge : ''}`}>
                          {tag.tagName}
                        </span>
                      </td>
                      <td><span className={classes.versionBadge}>v{tag.version}</span></td>
                      <td>
                        <Tooltip title={tag.modifiedDate ? new Date(tag.modifiedDate).toLocaleString() : '—'} arrow>
                          <span className={classes.timestampText}>
                            {tag.modifiedDate ? formatRelativeTime(tag.modifiedDate) : '—'}
                          </span>
                        </Tooltip>
                      </td>
                      <td className={classes.timestampText}>{tag.modifiedBy || tag.createdBy || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '2px' }}>
                          <Tooltip title={`Promote tag "${tag.tagName}"`} arrow>
                            <IconButton size='small' className={classes.actionIconBtn} onClick={() => openPromoteForm(tag)} disabled={isSaving}>
                              <EditIcon style={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={confirmDelete === tag.tagName ? 'Click again to confirm' : `Delete tag "${tag.tagName}"`} arrow>
                            <IconButton
                              size='small'
                              className={classes.deleteIconBtn}
                              disabled={isSaving}
                              style={confirmDelete === tag.tagName ? { color: 'var(--color-danger-600, #dc2626)' } : undefined}
                              onClick={() => {
                                if (confirmDeleteTimerRef.current) {
                                  clearTimeout(confirmDeleteTimerRef.current)
                                  confirmDeleteTimerRef.current = null
                                }
                                if (confirmDelete === tag.tagName) {
                                  setConfirmDelete(null)
                                  handleDelete(tag.tagName)
                                } else {
                                  setConfirmDelete(tag.tagName)
                                  confirmDeleteTimerRef.current = setTimeout(() => {
                                    setConfirmDelete(prev => prev === tag.tagName ? null : prev)
                                    confirmDeleteTimerRef.current = null
                                  }, 3000)
                                }
                              }}
                            >
                              <DeleteOutlineIcon style={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr className={classes.virtualRow}>
                    <td className={classes.tagNameCell}>
                      <span className={`${classes.versionBadge} ${classes.nextBadge}`}>next</span>
                    </td>
                    <td><span className={classes.versionBadge}>v{maxVersion}</span></td>
                    <td className={classes.timestampText}>—</td>
                    <td className={classes.timestampText}>system</td>
                    <td />
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {formMode && (
          <div className={classes.formCard}>
            <div className={classes.formHeader}>
              <span className={classes.formTitle}>
                {formMode === 'create' ? 'Create Tag' : `Promote Tag: ${form.tagName}`}
              </span>
              {isSaving && (
                <span className={classes.savingOverlay}>
                  <CircularProgress size={14} thickness={5} />
                  Saving…
                </span>
              )}
            </div>

            <div className={classes.formRow}>
              {formMode === 'create' && (
                <div className={classes.formField} style={{ maxWidth: '180px' }}>
                  <label className={classes.formLabel}>Tag Name</label>
                  <input
                    className={`${classes.formInput} ${classes.formInputMono}`}
                    value={form.tagName}
                    onChange={(e) => handleFormChange('tagName', e.target.value)}
                    placeholder='e.g. live, beta'
                    autoFocus
                    maxLength={MAX_TAG_NAME_LENGTH}
                    disabled={isSaving}
                  />
                  {tagNameError && <span className={classes.formError}>{tagNameError}</span>}
                </div>
              )}
              <div className={classes.formField} style={{ maxWidth: '160px' }}>
                <label className={classes.formLabel}>Version</label>
                <select
                  className={classes.formSelect}
                  value={form.version}
                  onChange={(e) => handleFormChange('version', e.target.value)}
                  disabled={isSaving}
                >
                  <option value=''>Select…</option>
                  {versionOptions.map(v => (
                    <option key={v} value={v}>v{v}{v === maxVersion ? ' (latest)' : ''}</option>
                  ))}
                </select>
              </div>
              <div className={classes.formField}>
                <label className={classes.formLabel}>Comment <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                <input
                  className={classes.formInput}
                  value={form.comment}
                  onChange={(e) => handleFormChange('comment', e.target.value)}
                  placeholder='Reason for this change…'
                  disabled={isSaving}
                />
              </div>
              <div className={classes.formField} style={{ maxWidth: '140px' }}>
                <label className={classes.formLabel}>Ticket # <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                <input
                  className={classes.formInput}
                  value={form.ticketNumber}
                  onChange={(e) => handleFormChange('ticketNumber', e.target.value)}
                  placeholder='SMT-1234'
                  disabled={isSaving}
                />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className={classes.attestRow}>
                <input
                  type='checkbox'
                  className={classes.attestCheckbox}
                  checked={form.attested}
                  onChange={(e) => handleFormChange('attested', e.target.checked)}
                  id='ddp-tag-attest'
                  disabled={isSaving}
                />
                <label htmlFor='ddp-tag-attest' className={classes.attestLabel}>
                  I attest that I have verified the correctness of the target version and intend to make this change.
                </label>
              </div>
              <div className={classes.formActions}>
                <SydButton size='sm' variant='secondary' onClick={closeForm} disabled={isSaving}>Cancel</SydButton>
                <SydButton size='sm' variant='primary' onClick={handleSubmit} disabled={!canSubmit}>
                  {formMode === 'create' ? 'Create' : 'Promote'}
                </SydButton>
              </div>
            </div>
          </div>
        )}

        <div className={classes.section} style={{ minHeight: 0, flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
          <span className={classes.sectionTitle}>History</span>
          <div className={classes.historyWrap} style={{ flex: '1 1 auto' }}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>Tag</th>
                  <th>From</th>
                  <th>To</th>
                  <th>User</th>
                  <th>Comment</th>
                  <th>Ticket</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr><td colSpan={8} className={classes.emptyText}>Loading…</td></tr>
                ) : !historyData?.history?.length ? (
                  <tr><td colSpan={8} className={classes.emptyText}>No history yet</td></tr>
                ) : historyData.history.map(entry => (
                  <tr key={entry.dataPipelineTagAuditId}>
                    <td>
                      <Tooltip title={new Date(entry.performedDate).toLocaleString()} arrow>
                        <span className={classes.timestampText}>
                          {formatRelativeTime(entry.performedDate)}
                        </span>
                      </Tooltip>
                    </td>
                    <td>
                      <span
                        className={`${classes.actionBadge} ${
                          entry.action === 'create' ? classes.actionCreate
                            : entry.action === 'promote' ? classes.actionPromote
                              : classes.actionDelete
                        }`}
                      >
                        {entry.action}
                      </span>
                    </td>
                    <td className={classes.tagNameCell}>{entry.tagName}</td>
                    <td>{entry.fromVersion != null ? `v${entry.fromVersion}` : '—'}</td>
                    <td>{entry.toVersion != null ? `v${entry.toVersion}` : '—'}</td>
                    <td className={classes.timestampText}>{entry.performedBy}</td>
                    <td>
                      {entry.comment
                        ? (
                          <Tooltip title={entry.comment} arrow>
                            <span className={classes.commentText}>{entry.comment}</span>
                          </Tooltip>
                        )
                        : <span className={classes.timestampText}>—</span>}
                    </td>
                    <td>
                      {entry.ticketNumber
                        ? <span className={classes.tagNameCell} style={{ fontSize: 'var(--text-size-xs)' }}>{entry.ticketNumber}</span>
                        : <span className={classes.timestampText}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        {toast && (
          <Alert onClose={() => setToast(null)} severity={toast.severity} variant='filled' elevation={6}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </Dialog>
  )
}

DataPipelineTagManagementModal.propTypes = {
  code: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
}

export default DataPipelineTagManagementModal
